import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "aoa_cookie_consent";
const TTL_MS = 365 * 24 * 60 * 60 * 1000;

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj.timestamp || Date.now() - obj.timestamp > TTL_MS) return null;
    return obj;
  } catch {
    return null;
  }
}

export default function CookieBanner({ settings }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!readConsent()) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener("reopen-cookie-banner", reopen);
    return () => window.removeEventListener("reopen-cookie-banner", reopen);
  }, []);

  const cb = settings?.cookie_banner;
  if (cb && cb.enabled === false) return null;

  const choose = (choice) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, timestamp: Date.now() }),
    );
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-50 bottom-4 right-4 left-4 md:left-auto md:max-w-md"
          data-testid="cookie-banner"
        >
          <div className="relative rounded-2xl border border-[hsl(var(--aoa-sky-light))]/40 bg-[hsl(var(--aoa-navy))]/95 backdrop-blur-md p-6 shadow-[0_30px_80px_-30px_rgba(10,31,68,0.6)] text-white">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 flex-none place-items-center rounded-full bg-[hsl(var(--aoa-sky-light))]/15">
                <Cookie className="h-5 w-5 text-[hsl(var(--aoa-sky-light))]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-base font-semibold">Cookies on AOA Somalia</h3>
                <p className="mt-2 text-sm text-slate-200/90 leading-relaxed">
                  {cb?.body ||
                    "AOA Somalia uses cookies to make the website work and to understand how visitors use our pages. We don't use cookies for marketing or third-party tracking. You can choose what to accept."}
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white"
                data-testid="cookie-dismiss"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                onClick={() => choose("all")}
                data-testid="cookie-accept-all"
                className="inline-flex items-center rounded-full bg-[hsl(var(--aoa-royal))] px-5 py-2 text-sm font-semibold hover:bg-[hsl(var(--aoa-sky))] transition-colors"
              >
                {cb?.accept_all_label || "Accept All"}
              </button>
              <button
                onClick={() => choose("essential")}
                data-testid="cookie-essential"
                className="inline-flex items-center rounded-full border border-white/30 px-5 py-2 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                {cb?.essential_label || "Essential Only"}
              </button>
              <Link
                to="/cookie-policy"
                onClick={() => setOpen(false)}
                data-testid="cookie-learn-more"
                className="ml-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-sky-light))] hover:underline"
              >
                {cb?.learn_more_label || "Learn More"}
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
